import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/api";
import { InquiryModal } from "./InquiryModal";

export const ProductGrid = () => {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Could not load the archive"))
      .finally(() => setLoaded(true));
  }, []);

  const available = products.filter((p) => p.status !== "sold");

  return (
    <section id="shop" data-testid="archive-section" className="px-6 py-24 md:px-12 md:py-28 lg:px-20">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-12 flex flex-wrap items-end justify-between gap-6"
      >
        <div>
          <p className="mb-4 inline-block rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-4 py-1 text-[10px] font-mono font-medium uppercase tracking-[0.25em]">
            01 — Fresh drops
          </p>
          <h2 data-testid="archive-heading" className="font-serif font-black uppercase leading-none tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            Shop the rack
          </h2>
        </div>
        <p data-testid="archive-count" className="text-xs font-mono uppercase tracking-[0.2em] text-[#F5F2EB]/60">
          {available.length} available — every piece one of one
        </p>
      </motion.div>

      {loaded && available.length === 0 && (
        <p data-testid="archive-empty" className="rounded-xl border-2 border-[#F5F2EB] bg-[#141414] p-12 text-center text-sm font-mono text-[#F5F2EB]/60">
          The rack is empty — new pieces drop soon.
        </p>
      )}

      <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
        {available.map((p, i) => (
          <motion.button
            key={p.id}
            type="button"
            data-testid={`product-card-${p.id}`}
            onClick={() => setActive(p)}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
            className="group overflow-hidden rounded-xl border-2 border-[#F5F2EB] bg-[#141414] text-left transition-all duration-200 hover:-translate-y-1 hover:shadow-[5px_5px_0_#F5F2EB]"
          >
            <div className="relative aspect-square overflow-hidden border-b-2 border-[#F5F2EB]">
              <img
                src={p.image}
                alt={p.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute left-3 top-3 rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-3 py-1 text-[9px] font-mono font-semibold uppercase tracking-[0.2em]">
                {p.category}
              </span>
              {p.status === "reserved" && (
                <span
                  data-testid={`product-reserved-badge-${p.id}`}
                  className="absolute right-3 top-3 rounded-full border-2 border-[#F5F2EB] bg-[#FFD23F] px-3 py-1 text-black text-[9px] font-mono font-semibold uppercase tracking-[0.2em]"
                >
                  Reserved
                </span>
              )}
            </div>
            <div className="flex items-start justify-between gap-2 p-3">
              <div>
                <p className="font-serif text-sm font-extrabold uppercase leading-tight">{p.title}</p>
                <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.15em] text-[#F5F2EB]/50">
                  {p.size} — {p.condition}
                </p>
              </div>
              <p className="shrink-0 rounded-full border-2 border-[#F5F2EB] bg-[#FF3B1F] px-3 py-1 text-xs font-mono font-semibold text-white">
                ${p.price}
              </p>
            </div>
          </motion.button>
        ))}
      </div>

      <InquiryModal product={active} onClose={() => setActive(null)} />
    </section>
  );
};
